'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuthStore } from '@/lib/store/auth-store'
import { useAppStore } from '@/lib/store/app-store'
import type { Currency } from '@/lib/store/app-store'
import { cn, ICONS, TIMEZONES, CURRENCIES, getTzAbbr } from '@/lib/utils'
import { NavSearchBar } from './nav-search-bar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const NAV_LINKS = [
  { label: 'Leagues', href: '/leagues' },
  { label: 'Games', href: '/games' },
  { label: 'AI', href: '/ai' },
  { label: 'Stats', href: '/stats' },
  { label: 'User Leagues', href: '/user-leagues' },
  { label: 'Contact', href: '/contact' },
] as const

function IconMask({ src, className }: { src: string; className?: string }) {
  return (
    <span
      className={cn('inline-block', className)}
      style={{
        maskImage: `url(${src})`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskImage: `url(${src})`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
      }}
    />
  )
}

export function Navbar() {
  const { session } = useAuthStore()
  const { timezone, currency, setTimezone, setCurrency } = useAppStore()
  const pathname = usePathname()

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 flex flex-wrap items-center gap-y-1 border-b border-white/15 px-4 py-2.5 shadow-[0_4px_10px_-2px_rgba(0,0,0,0.6)] sm:gap-3 sm:px-10 sm:py-4"
      style={{ background: '#000000' }}
    >
      <Link href="/home" className="flex shrink-0 items-center gap-2 no-underline">
        <img src={ICONS.logo} alt="Goal Archive" className="h-6 w-6 rounded-md object-cover" />
        <span className="text-sm font-semibold text-white">Goal Archive</span>
      </Link>

      <div className="min-w-0 flex-1 px-2 sm:px-4">
        <NavSearchBar />
      </div>

      <div className="w-full overflow-x-auto scrollbar-none">
        <div className="flex min-w-max items-center gap-4 pt-1.5">
          {NAV_LINKS.map((l) => {
            const active = pathname === l.href || pathname.startsWith(`${l.href}/`)
            return (
              <Link
                key={l.href}
                href={l.href}
                className={cn(
                  'tab-nav-link shrink-0 border-b-2 pb-1 text-sm font-medium no-underline transition-colors',
                  active ? 'tab-nav-link-active border-white' : 'border-transparent'
                )}
              >
                {l.label}
              </Link>
            )
          })}

          <Link
            href="/fpl"
            className={cn(
              'tab-nav-link shrink-0 border-b-2 pb-1 text-sm font-medium no-underline transition-colors',
              pathname.startsWith('/fpl') ? 'tab-nav-link-active border-white' : 'border-transparent'
            )}
          >
            FPL
          </Link>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Settings"
                className="tab-nav-link flex shrink-0 items-center gap-1 border-b-2 border-transparent pb-1 text-sm font-medium transition-colors"
              >
                <IconMask src={ICONS.settings} className="h-3.5 w-3.5 bg-current" />
                Settings
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-3">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <img src={ICONS.globe} alt="" className="h-3 w-3 brightness-0 invert" />
                    Time zone
                  </label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger size="sm" className="w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-48">
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value} className="text-xs">
                          {tz.label}
                          <span className="text-muted-foreground font-mono">{getTzAbbr(tz.value)}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Currency
                  </label>
                  <Select value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                    <SelectTrigger size="sm" className="w-full text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.value} value={c.value} className="text-xs">
                          {c.symbol} {c.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Link
            href={session ? '/profile' : '/signin'}
            className={cn(
              'tab-nav-link flex shrink-0 items-center gap-1 border-b-2 pb-1 text-sm font-medium no-underline transition-colors',
              pathname === '/profile' || pathname === '/signin' ? 'tab-nav-link-active border-white' : 'border-transparent'
            )}
          >
            <IconMask src={session ? ICONS.userSquare : ICONS.login} className="h-3.5 w-3.5 bg-current" />
            {session ? 'Account' : 'Sign in'}
          </Link>
        </div>
      </div>
    </nav>
  )
}
